import React, { Fragment, useState } from 'react';
import { Box, TextField } from '@mui/material';
import { DraggableComponentProps } from 'features/document/constants';
import { isTableElement } from 'utils/typeGuards';
import {
  EditableCell,
  MAX_COLUMNS_PER_TABLE,
  MIN_COLUMNS_PER_TABLE,
  MIN_ROWS_PER_TABLE,
  TableColumn,
  TableContent,
  handleAddColumn,
  handleAddHeader,
  handleAddRow,
  handleApplyBackgroundColor,
  handleMergeCells,
  handleRemoveColumn,
  handleRemoveRow,
  handleUnmergeCells,
} from './Table.utils';
import { selectSelectedItem, updateContentElementById } from 'features/document/documentSlice';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import TableToolbar from 'features/document/components/SelectedItem/components/TableToolbar';
import './Table.css';
import { StyledTable, Th } from './Table.styled';
import { colorPalette } from 'styles/partials/colors';

export default function Table({ currentElement }: DraggableComponentProps) {
  if (!isTableElement(currentElement)) {
    return null;
  }

  const dispatch = useAppDispatch();
  const selector = useAppSelector;
  const selectedItem = selector(selectSelectedItem);

  const table = currentElement.content;
  const { columns, rows, cells } = table;

  const [activeCellId, setActiveCellId] = useState<string>('');
  const [activeRowId, setActiveRowId] = useState<string>('');
  const [activeColumnId, setActiveColumnId] = useState<string>('');
  const [activeHeaderId, setActiveHeaderId] = useState<string>('');
  const [editableCell, setEditableCell] = useState<EditableCell | null>(null);

  const disableAddColumns = columns.length === MAX_COLUMNS_PER_TABLE || activeHeaderId !== '';
  const disableRemoveColumns = columns.length === MIN_COLUMNS_PER_TABLE || activeHeaderId !== '';
  const disableAddRows = activeHeaderId !== '';
  const disableRemoveRows = rows.length === MIN_ROWS_PER_TABLE || activeHeaderId !== '';
  const disableMergeCells = activeHeaderId !== '' || editableCell?.colSpan === columns.length;
  const disableUnmergeCells = editableCell?.colSpan === 1 || activeHeaderId !== '';

  const isTableSelected = selectedItem?.data?.id == currentElement.id;
  const isCellActive = activeRowId !== '' && activeColumnId !== '' && isTableSelected;
  const showToolbar = isCellActive && isTableSelected;

  const resetActiveHeaderId = () => {
    setActiveHeaderId('-1');
  };

  const handleCellClick = (rowId: string, cellId: string, columnId: string | null, cellColSpan?: number) => {
    setActiveCellId(cellId);
    if (columnId) {
      setActiveRowId(rowId);
      setActiveColumnId(columnId);
      setEditableCell({ rowId, cellId, colSpan: cellColSpan || 1 });
      setActiveHeaderId('');
    }
  };

  const handleHeaderClick = (columnId: string | null) => {
    if (columnId) {
      setActiveHeaderId(columnId);
    }
  };

  const updateTableContent = (newTable: TableContent) => {
    dispatch(
      updateContentElementById({
        content: newTable,
        style: {},
      })
    );
  };

  const handleEditCell = (content: string) => {
    if (editableCell) {
      const newCells = { ...cells };
      newCells[editableCell.cellId] = { ...newCells[editableCell.cellId], content };

      updateTableContent({
        ...table,
        cells: newCells,
      });
    }
  };

  const handleEditHeader = (newHeader: string) => {
    if (activeColumnId) {
      const newColumns = [...columns];
      const updatedColumnIndex = newColumns.findIndex(column => column.id === activeHeaderId);

      if (updatedColumnIndex !== -1) {
        const updatedColumn = { ...newColumns[updatedColumnIndex], header: newHeader };
        newColumns[updatedColumnIndex] = updatedColumn;

        updateTableContent({
          ...table,
          columns: newColumns,
        });
      }
    }
  };

  const getColumnIdFromCellId = (cellId: string, columns: TableColumn[]): string | null => {
    for (const column of columns) {
      if (column.cellIds.includes(cellId)) {
        return column.id;
      }
    }
    return null;
  };

  const renderCellContent = (content: string, backgroundColor?: string) => {
    return (
      <TextField
        type='text'
        value={content}
        onChange={e => handleEditCell(e.target.value)}
        sx={{
          '& .MuiOutlinedInput-input ': {
            padding: '8px 8px 6px 8px',
          },
          '& .MuiOutlinedInput-notchedOutline ': {
            border: 'none',
          },
          width: '100%',
          backgroundColor: backgroundColor || 'transparent',
        }}
      />
    );
  };

  const renderTableHeader = () => {
    return (
      <thead>
        <tr>
          {columns.map(column => (
            <Th key={column.id}>
              <TextField
                type='text'
                value={column.header}
                onFocus={() => handleHeaderClick(column.id)}
                onChange={e => handleEditHeader(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-input ': {
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: '500',
                    textAlign: 'center',
                    padding: '8px',
                  },
                  '& .MuiOutlinedInput-notchedOutline ': {
                    border: 'none',
                  },
                  width: '100%',
                  backgroundColor: column.backgroundColor || '#3067A6',
                }}
              />
            </Th>
          ))}
        </tr>
      </thead>
    );
  };

  const renderTableBody = () => {
    return (
      <tbody>
        {rows.map(row => (
          <tr className='row' key={row.id}>
            {row.cellIds.map(cellId => {
              const cell = cells[cellId];
              const columnId = getColumnIdFromCellId(cellId, columns);
              return (
                <td
                  className='cell'
                  colSpan={cell.colSpan}
                  key={cell.id}
                  onFocus={() => handleCellClick(row.id, cellId, columnId, cell.colSpan)}
                  style={{ display: cell.hidden ? 'none' : 'table-cell' }}
                >
                  {renderCellContent(cell.content, cell.backgroundColor)}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    );
  };

  return (
    <Fragment>
      <Box
        left='50%'
        position='absolute'
        right='50%'
        sx={{
          opacity: !showToolbar ? 0 : 1,
          transition: 'opacity 0.3s ease',
        }}
        top='-40px'
        zIndex={99999}
      >
        <TableToolbar
          disableAddColumns={disableAddColumns}
          disableAddRows={disableAddRows}
          disableMergeCells={disableMergeCells}
          disableUnmergeCells={disableUnmergeCells}
          disableRemoveColumns={disableRemoveColumns}
          disableRemoveRows={disableRemoveRows}
          hideTooltips={!showToolbar}
          handleApplyBackgroundColor={color => {
            const isHeader = activeHeaderId !== '' && activeHeaderId !== '-1';
            handleApplyBackgroundColor(table, editableCell, color, isHeader, activeHeaderId, updateTableContent);
          }}
          handleAddHeader={() => {
            handleAddHeader(table, activeRowId, colorPalette.tableBlueCell, updateTableContent);
          }}
          handleAddRowAbove={() => handleAddRow(table, activeRowId, 'above', updateTableContent)}
          handleAddRowBelow={() => handleAddRow(table, activeRowId, 'below', updateTableContent)}
          handleAddColumnBefore={() => handleAddColumn(table, activeColumnId, 'before', updateTableContent)}
          handleAddColumnAfter={() => handleAddColumn(table, activeColumnId, 'after', updateTableContent)}
          handleMergeCells={() => {
            resetActiveHeaderId();
            handleMergeCells(table, activeRowId, activeColumnId, activeCellId, updateTableContent);
          }}
          handleRemoveRow={() => {
            resetActiveHeaderId();
            handleRemoveRow(table, activeRowId, updateTableContent);
          }}
          handleRemoveColumn={() => {
            resetActiveHeaderId();
            handleRemoveColumn(table, activeColumnId, activeCellId, updateTableContent);
          }}
          handleUnmergeCells={() => {
            resetActiveHeaderId();
            handleUnmergeCells(table, activeRowId, activeCellId, updateTableContent);
          }}
        />
      </Box>
      <StyledTable>
        {renderTableHeader()}
        {renderTableBody()}
      </StyledTable>
    </Fragment>
  );
}
